/**
 * Phase 129 (Plan 129-10, D-15 / D-17 / D-20) — the Release flags card of the Engineering tab.
 *
 * The project's own release flags (the server lists only flags whose ticket resolves to exactly
 * one task of this project), drawn by the same `ReleaseFlagTable` the admin page uses, with the
 * same three actions: Switch on (mark verified), Switch off and Reset override. Each action sits
 * behind its confirm and acts at once; the card shares no Save with the General form (D-20).
 *
 * Renders nothing while loading, on error, and when the project has no flags.
 */
import type { ReactElement } from 'react';
import { ReleaseFlagTable } from '../release-flags/ReleaseFlagTable';
import {
  useProjectReleaseFlags,
  useResetProjectFlagOverride,
  useSwitchProjectFlagOff,
  useSwitchProjectTicketOn,
} from '../../hooks/useProjectReleaseFlags';

export interface ProjectReleaseFlagsCardProps {
  projectId: number;
}

export function ProjectReleaseFlagsCard({
  projectId,
}: ProjectReleaseFlagsCardProps): ReactElement | null {
  const flagsQ = useProjectReleaseFlags(projectId);
  const switchOff = useSwitchProjectFlagOff(projectId);
  const reset = useResetProjectFlagOverride(projectId);
  const switchOn = useSwitchProjectTicketOn(projectId);

  const rows = flagsQ.data;
  if (flagsQ.isLoading || flagsQ.error || !rows || rows.length === 0) return null;

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-4"
      data-testid="project-release-flags-card"
    >
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Release flags</h2>
        <p className="text-sm text-gray-600">
          The flags of this project&apos;s tickets. A flag is on in staging and off in production
          until its ticket is verified.
        </p>
      </div>
      <ReleaseFlagTable
        rows={rows}
        actions={{
          switchOn: (ticket) => switchOn.mutateAsync(ticket),
          switchOff: (key) => switchOff.mutateAsync(key),
          reset: (key) => reset.mutateAsync(key),
          switchOnPending: switchOn.isPending,
          switchOffPending: switchOff.isPending,
          resetPending: reset.isPending,
        }}
      />
    </section>
  );
}

export default ProjectReleaseFlagsCard;
